/**
 * Office-domain (Greenleaf Commercial Officer) API types.
 * Mirrors contracts/api/v1/kyc.yaml and the customer-service serializers
 * (`_kyc_submission_dict`, submission payload snapshot, decision responses).
 * NIDA is ALWAYS masked in list responses; the detail payload snapshot is
 * immutable and unmasked — UI must mask before display (TASK-005 packet).
 */

export type KycStatus = "pending" | "approved" | "correction_requested";

/** Queue row — `_kyc_submission_dict(sub, lead, masked=True)`. */
export interface KycQueueRow {
  id: number;
  lead_id: number;
  status: KycStatus;
  customer_name: string | null;
  phone: string | null;
  /** MASKED by the server (first 4 + last 2 visible). */
  national_id: string | null;
  ward: string | null;
  customer_id: number | null;
  decided_by: number | null;
  decided_at: string | null;
  decision_note: string | null;
  decision_reasons: string[] | null;
  created_at: string | null;
}

/** GET /kyc/submissions?status=... response. */
export interface KycQueueResponse {
  submissions: KycQueueRow[];
  total: number;
}

/** Immutable lead-capture snapshot written at submission time. */
export interface KycPayloadSnapshot {
  first_name?: string;
  last_name?: string;
  phone?: string;
  national_id?: string;
  ward?: string;
  address?: string;
  gps_lat?: number | null;
  gps_lng?: number | null;
  gps_accuracy_m?: number | null;
  geofence_state?: "in_range" | "out_of_range" | "acquiring" | "unknown";
  /** Server-relative upload paths carried into the snapshot at submit time
      (TASK-007a); null/absent when photos were never uploaded. */
  photo_customer_url?: string | null;
  photo_house_url?: string | null;
}

/** GET /kyc/submissions/:id — queue row + payload snapshot (unmasked). */
export interface KycSubmissionDetail extends Omit<KycQueueRow, "national_id"> {
  /** Detail serializer returns the unmasked NIDA — mask before display. */
  national_id: string | null;
  payload: KycPayloadSnapshot;
}

export interface KycSubmissionDetailResponse {
  submission: KycSubmissionDetail;
}

/** Canonical correction reasons (ratified v1, kyc.yaml). */
export const KYC_CORRECTION_REASONS = [
  "photo_unclear",
  "nida_mismatch",
  "address_incomplete",
  "gps_out_of_range",
  "other",
] as const;
export type KycCorrectionReason = (typeof KYC_CORRECTION_REASONS)[number];

export const KYC_CORRECTION_REASON_LABELS: Record<KycCorrectionReason, string> = {
  photo_unclear: "Photo unclear",
  nida_mismatch: "NIDA mismatch",
  address_incomplete: "Address incomplete",
  gps_out_of_range: "GPS out of range",
  other: "Other",
};

/** POST /kyc/submissions/:id/decision request body (kyc.yaml). */
export interface KycDecisionRequest {
  action: "approve" | "request-correction";
  reasons?: KycCorrectionReason[];
  note?: string;
}

/** Approve response (200) — also returned on idempotent replay. */
export interface KycApproveResponse {
  message: string;
  submission: KycQueueRow;
  customer: { id: number; customer_number: string };
  contract: { id: number; contract_number: string; is_draft: boolean };
  /** Present on idempotent replay instead of customer/contract objects. */
  customer_id?: number | null;
  contract_id?: number | null;
}

/** Request-correction response (200). */
export interface KycCorrectionResponse {
  message: string;
  submission: KycQueueRow;
}
